import { Injectable } from '@angular/core'
import {
    ConfirmedSessionsResponse,
    FavoritesAvailabilityPartner,
    FavoritesAvailabilityResponse,
    FavoritesAvailabilitySessionInfo,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
} from '@app/core/_models'
import { UtilsService } from '@app/core/_services'

import { SessionStateService } from '@app/core/_services/session-state.service'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'
import {
    DURATION_FILTER_INCLUDE_25,
    DURATION_FILTER_INCLUDE_50,
    DURATION_FILTER_INCLUDE_75,
    DURATION_FILTER_INCLUDE_ALL,
} from '@app/shared-availability-list/components/availability-list-controls/availability-list-controls.component'
import { AvailabilitySessionListItem } from '@app/shared-availability-list/components/availability-list-item/availability-list-item.component'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'

const SESSION_DURATION_50_MINS_LABEL: string = '50m'
const SESSION_DURATION_25_MINS_LABEL: string = '25m'
const SESSION_DURATION_75_MINS_LABEL: string = '75m'

export interface FavoritesAvailabilityList {
    dayLabel: string
    times: FavoritesAvailabilityListTimes[]
}

export interface FavoritesAvailabilityListTimes {
    timeLabel: string
    sessionTime: number
    sessions25: AvailabilitySessionListItem[]
    sessions50: AvailabilitySessionListItem[]
    sessions75: AvailabilitySessionListItem[]
}

@Injectable({
    providedIn: 'root',
})
export class FavoritesListService {
    constructor(
        private utilsService: UtilsService,
        private timeUtils: TimeUtilitiesService,
        private sessionStateService: SessionStateService,
        private dateFormat: UserDateFormattingService,
    ) {}

    public populateListFromFavoritesAvailabilityResponse(
        favoriteUsersAvailability: FavoritesAvailabilityResponse,
        currentList: AvailabilitySessionListItem[],
        timezone: string,
    ) {
        const partners = favoriteUsersAvailability.availability.partners || {}
        const sessions = favoriteUsersAvailability.availability.availableSessions || []

        for (let session of sessions) {
            const partner = partners[session.userId]

            if (partner.saved) {
                currentList.push({
                    timeLabel: this.dateFormat.format(session.sessionTime, 'h:mma', timezone),
                    externalId: null,
                    sessionTime: session.sessionTime,
                    sessionDuration: session.duration,
                    sessionState: null,
                    sessionDurationLabel: this.getSessionDurationLabel(session.duration),
                    actionComplete: false,
                    confirmed: false,
                    cancelConfirm: false,
                    profilePhoto: partner.profilePhoto,
                    partnerUserId: session.userId,
                    partnerDisplayName: partner.displayName,
                    partnerProfileSlug: partner.profileSlug,
                    saved: true,
                    sharedAvailability: partner.sharedAvailability,
                    activityType: session.activityType,
                    partnerPreferences: session.preferences,
                    quietModeMatchAllowed: partner.quietModeMatchAllowed,
                })
            }
        }
    }

    public populateListFromConfirmedSessionsResponse(
        confirmedSesions: ConfirmedSessionsResponse[],
        currentList: AvailabilitySessionListItem[],
        startTimeInMsecs: number,
        endTimeInMsecs: number,
        timezone: string,
    ) {
        let confirmedList = confirmedSesions || []

        for (let session of confirmedList) {
            if (
                !this.utilsService.hasSessionTimePassed(
                    session.sessionTime,
                    this.utilsService.getCurrentTimestamp(),
                ) &&
                session.sessionTime >= startTimeInMsecs &&
                session.sessionTime <= endTimeInMsecs &&
                this.sessionStateService.isLockedInState(session.state)
            ) {
                currentList.push({
                    timeLabel: this.dateFormat.format(session.sessionTime, 'h:mma', timezone),
                    externalId: session.externalId,
                    sessionTime: session.sessionTime,
                    sessionDuration: session.duration,
                    sessionState: session.state,
                    sessionDurationLabel: this.getSessionDurationLabel(session.duration),
                    actionComplete: false,
                    confirmed: true,
                    cancelConfirm: false,
                    profilePhoto: session.partnerPhoto,
                    partnerUserId: session.partnerId,
                    partnerDisplayName: session.partnerDisplayName,
                    partnerProfileSlug: session.partnerProfileUrl,
                    saved: session.saved,
                    sharedAvailability: session.sharedAvailability,
                    activityType: session.activityType,
                    partnerPreferences: session.partnerPreferences,
                    quietModeMatchAllowed: true, // We do not receive this info currently from the API, default to true as it won't have any effect here
                })
            }
        }
    }

    public populateFilteredDisplayList(
        filteredFavoritesSessionList: FavoritesAvailabilityList[],
        completeList: AvailabilitySessionListItem[],
        timezone: string,
        filterOption,
        quietModeSelected,
    ) {
        filteredFavoritesSessionList.length = 0
        let tempList: FavoritesAvailabilityList[] = []

        completeList.sort(
            (a, b) => a.sessionTime - b.sessionTime || a.sessionDuration - b.sessionDuration,
        )
        for (let meeting of completeList) {
            if (
                !this.filterSessionDuration(meeting.sessionDuration, filterOption) &&
                !this.filterSessionBasedOnQuietModeSettings(meeting, quietModeSelected)
            ) {
                this.addSessionToList(tempList, meeting, timezone)
            }
        }
        filteredFavoritesSessionList.push(...tempList)
    }

    public removeAvailableSessionsFromList(currentList: AvailabilitySessionListItem[]) {
        return currentList.filter((session) => session.confirmed !== false)
    }

    public removeConfirmedSessionsFromList(currentList: AvailabilitySessionListItem[]) {
        return currentList.filter((session) => session.confirmed !== true)
    }

    private getSessionDurationLabel(sessionDuration: number) {
        if (sessionDuration === SESSION_DURATION_50_MINS) {
            return SESSION_DURATION_50_MINS_LABEL
        } else if (sessionDuration == SESSION_DURATION_75_MINS) {
            return SESSION_DURATION_75_MINS_LABEL
        } else {
            return SESSION_DURATION_25_MINS_LABEL
        }
    }

    private filterSessionDuration(duration: number, filterOption): boolean {
        if (filterOption === DURATION_FILTER_INCLUDE_ALL) {
            return false
        }
        if (filterOption === DURATION_FILTER_INCLUDE_50 && duration === SESSION_DURATION_50_MINS) {
            return false
        }
        if (filterOption === DURATION_FILTER_INCLUDE_25 && duration === SESSION_DURATION_25_MINS) {
            return false
        }

        if (filterOption === DURATION_FILTER_INCLUDE_75 && duration === SESSION_DURATION_75_MINS) {
            return false
        }
        return true
    }

    private filterSessionBasedOnQuietModeSettings(
        meeting: AvailabilitySessionListItem,
        quietModeSelected: boolean,
    ): boolean {
        /* Do not include sessions in this list if the following conditions are met:
		1. Potential partner has quietModeMatchAllowed setting set to False
		2. Current user has selected quietMode
		3. Potential partner's session is not set to Quiet Mode
		*/
        if (
            !meeting.quietModeMatchAllowed &&
            quietModeSelected &&
            !meeting.partnerPreferences.quietMode.value
        ) {
            return true
        }
        return false
    }

    private addSessionToList(filteredFavoritesSessionList, meeting, timezone: string) {
        let currentDate = this.genereateDateLabel(meeting.sessionTime, timezone)

        if (
            filteredFavoritesSessionList.length === 0 ||
            filteredFavoritesSessionList[filteredFavoritesSessionList.length - 1].dayLabel !==
                currentDate
        ) {
            let timeEntry: FavoritesAvailabilityListTimes = {
                sessionTime: meeting.sessionTime,
                timeLabel: meeting.timeLabel,
                sessions25: [],
                sessions50: [],
                sessions75: [],
            }

            if (meeting.sessionDuration == SESSION_DURATION_25_MINS) {
                timeEntry.sessions25.push(meeting)
            } else if (meeting.sessionDuration == SESSION_DURATION_50_MINS) {
                timeEntry.sessions50.push(meeting)
            } else {
                timeEntry.sessions75.push(meeting)
            }

            filteredFavoritesSessionList.push({
                dayLabel: currentDate,
                times: [timeEntry],
            })
        } else {
            const lastFavoritesSession =
                filteredFavoritesSessionList[filteredFavoritesSessionList.length - 1]
            const lastSessionTime =
                lastFavoritesSession.times[lastFavoritesSession.times.length - 1]
            const lastTimeLabel = lastSessionTime.timeLabel

            if (lastTimeLabel !== meeting.timeLabel) {
                let timeEntry: FavoritesAvailabilityListTimes = {
                    sessionTime: meeting.sessionTime,
                    timeLabel: meeting.timeLabel,
                    sessions25: [],
                    sessions50: [],
                    sessions75: [],
                }

                if (meeting.sessionDuration == SESSION_DURATION_25_MINS) {
                    timeEntry.sessions25.push(meeting)
                } else if (meeting.sessionDuration == SESSION_DURATION_50_MINS) {
                    timeEntry.sessions50.push(meeting)
                } else {
                    timeEntry.sessions75.push(meeting)
                }
                filteredFavoritesSessionList[filteredFavoritesSessionList.length - 1].times.push(
                    timeEntry,
                )
            } else {
                const lastFavoritesSession =
                    filteredFavoritesSessionList[filteredFavoritesSessionList.length - 1]
                const times = lastFavoritesSession.times
                const slot = times[times.length - 1]

                if (meeting.sessionDuration == SESSION_DURATION_25_MINS) {
                    if (!this.handledDuplicateEntry(slot.sessions25, meeting)) {
                        slot.sessions25.push(meeting)
                    }
                } else if (meeting.sessionDuration == SESSION_DURATION_50_MINS) {
                    if (!this.handledDuplicateEntry(slot.sessions50, meeting)) {
                        slot.sessions50.push(meeting)
                    }
                } else {
                    if (!this.handledDuplicateEntry(slot.sessions75, meeting)) {
                        slot.sessions75.push(meeting)
                    }
                }
            }
        }
    }

    private handledDuplicateEntry(
        sessionList: AvailabilitySessionListItem[],
        sessionToCheck: AvailabilitySessionListItem,
    ): boolean {
        for (let session of sessionList) {
            if (
                session.sessionTime == sessionToCheck.sessionTime &&
                session.sessionDuration == sessionToCheck.sessionDuration &&
                session.partnerUserId == sessionToCheck.partnerUserId
            ) {
                // This can occur if a favorited user, that is sharing their calendar, is a currnt
                // match, but not locked in. If this happens, only preset the matched session and not
                // the available one
                if (sessionToCheck.confirmed) {
                    Object.assign(session, sessionToCheck)
                }
                return true
            }
        }
        return false
    }

    private genereateDateLabel(sessionTime: number, timezone: string): string {
        if (this.timeUtils.isToday(sessionTime, timezone)) {
            return moment(sessionTime).tz(timezone).format('[Today], MMMM D')
        } else if (this.timeUtils.isTomorrow(sessionTime, timezone)) {
            return moment(sessionTime).tz(timezone).format('[Tomorrow], MMMM D')
        } else return moment(sessionTime).tz(timezone).format('dddd, MMMM D')
    }
}
